// Tiny global store for SEO overrides loaded from /api/tools at boot.
//
// Why not React Context? <Seo> is mounted by every page, including
// pages that already sit deep in their own provider tree. A plain
// pub/sub keeps things simple and avoids re-rendering the whole tree
// when an admin updates an override (we only re-render the Helmet on
// pages that actually consume the relevant path).
//
// Shape of the store:
//   { '/box/basic': { 'pt': {...}, '*': {...} },
//     '/':          { '*':  {...} },
//     ... }
//
// Lookup precedence (most specific first):
//   1. exact (path, locale) match
//   2. (path, '*') match
//   3. fall through to the i18n default in <Seo>

let _store = {};
const _listeners = new Set();

export function setSeoOverrides(map) {
  _store = map && typeof map === 'object' ? map : {};
  for (const fn of _listeners) {
    try { fn(_store); } catch (_) { /* noop */ }
  }
}

export function getSeoOverrides() {
  return _store;
}

// Returns the partial override for a given (path, locale) pair, or null
// when no override exists. Empty/null fields in the row mean "use the
// i18n default for that field"; callers must still provide their own
// fallback per field.
export function getSeoOverride(path, lang) {
  const byLocale = _store[path];
  if (!byLocale) return null;
  return byLocale[lang] || byLocale['*'] || null;
}

// Subscribe to override updates (used by Admin's editor so a save shows
// up live in <head> on every open tab without a reload).
export function subscribeSeoOverrides(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

// Optimistic write — used by the admin editor to update the local store
// the moment the API call resolves, so the editor preview reflects the
// change immediately.
export function patchSeoOverrideLocal(path, locale, fields) {
  const next = { ..._store };
  if (!next[path]) next[path] = {};
  next[path] = { ...next[path], [locale]: { ...next[path][locale], ...fields } };
  setSeoOverrides(next);
}

export function removeSeoOverrideLocal(path, locale) {
  if (!_store[path] || !_store[path][locale]) return;
  const next = { ..._store };
  next[path] = { ...next[path] };
  delete next[path][locale];
  if (Object.keys(next[path]).length === 0) delete next[path];
  setSeoOverrides(next);
}
